import React, { FC, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { useMediaQuery } from "@mui/material";

import { useWhitelistingPausedRedirect,WHITELISTING_PAUSED_HINT } from "@hooks/useWhitelistingPausedRedirect";
import { useWhitelistingSuspended } from "@hooks/useWhitelistingSuspended";

import { GeneralLogo, MobileMenuLogo } from "@/assets/svg";
import { AccountType, useAccountType } from "@/contexts/AccountTypeContext";
import { useAuth } from "@/hooks/auth";
import { useActionRequiredCount } from "@/hooks/useActionRequiredCount";
import { usePresenceHeartbeat } from "@/hooks/usePresenceHeartbeat";
import AccountMenu from "@/layouts/components/AccountMenu";
// Global banners + their modal hosts. Desktop mounts these inside SecondHeader;
// on mobile/tablet (<1150px) SecondHeader isn't rendered, so they must be
// mounted here or they'd never show on phones/tablets.
import AccountRecoveredGlobalBanner from "@/layouts/components/AccountRecoveredGlobalBanner";
import BetaGlobalBanner from "@/layouts/components/BetaGlobalBanner";
import CancelledGlobalBanner from "@/layouts/components/CancelledGlobalBanner";
import DisconnectChannelsReminderBanner from "@/layouts/components/DisconnectChannelsReminderBanner";
import DowngradeSuccessBanner from "@/layouts/components/DowngradeSuccessBanner";
import GlobalIncidentBanner from "@/layouts/components/GlobalIncidentBanner";
import GlobalUpdatePaymentModal from "@/layouts/components/GlobalUpdatePaymentModal";
import PastDueGlobalBanner from "@/layouts/components/PastDueGlobalBanner";
import PendingDowngradeGlobalModal from "@/layouts/components/PendingDowngradeGlobalModal";
import PendingEmailGlobalBanner from "@/layouts/components/PendingEmailGlobalBanner";
import WhitelistingSuspendedGlobalBanner from "@/layouts/components/WhitelistingSuspendedGlobalBanner";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { toggleCart } from "@/store/reducers/cart";
import { colors, components } from "@/styles/design-system";
import { ERoutes } from "@/utils/constants";

/**
 * MobileHeader — the fluid phone + tablet site shell (replaces the old
 * `SecondaryBurgerMenu`). Rendered by SecondRootLayout ONLY below 1150px;
 * desktop (≥1150) keeps the untouched `SecondHeader`. (Swap is 1150, not the
 * global md=1024, because the desktop nav overlaps the cart between 1024–1150.)
 *
 * Only the main menu (☰) is a mobile-specific drawer. The account menu is the
 * SHARED desktop `AccountMenu` dropdown — same trigger, styling, and items at
 * every width; it just scrolls when taller than the viewport. Do not fork it
 * into a mobile variant again (user decision 2026-07-19: the two menus must
 * not diverge). Tablet is a roomier phone — same one-row bar + ☰ drawer:
 *  - logged in  → logo · [Claims] [Cart] [AccountMenu] [☰→main]
 *  - logged out → logo · [Cart] · phone:[Sign in→login] / tablet:[Log in][Sign up] · [☰→main]
 * Tablet differs only in: full wordmark logo, 380px drawer, taller bar, both auth buttons.
 * Phone's primary CTA is Sign in (opens login); sign-up stays in the drawer.
 *
 * Breakpoint note: phone↔tablet split at 600 is a JS branch (rarely crossed on
 * a real device). The mobile↔desktop split at 1150 lives in SecondRootLayout.
 * No user-agent sniffing. Heights use dvh so the drawers behave on iOS Safari.
 */

type Props = {
  handleOpenDialog: (isLogin: boolean) => void;
};

const BROWN = colors.brown.primary; // #59412e
const BLUE = colors.link.blue; // #9cd3fd
const BLUE_DEEP = colors.accent.blueDeep; // #193e5b
const SIGNUP_BLUE = colors.accent.blue; // solid blue (no gradient)
const ICON_GOLD = colors.text.link; // #ebc28e — gold icon stroke on the dark drawer
const GOLD_BTN = "linear-gradient(135deg, #ffd29b 0%, #ffebb5 100%)"; // brand golden button (design-system buttonPrimary)

/* ── inline icon helper ── */
const Ic: FC<{ d: string | string[]; size?: number; fill?: string; stroke?: string; sw?: number; style?: React.CSSProperties }> = ({
  d, size = 18, fill = "none", stroke = "currentColor", sw = 1.6, style,
}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={fill} stroke={stroke} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, ...style }}>
    {(Array.isArray(d) ? d : [d]).map((p, i) => <path key={i} d={p} />)}
  </svg>
);

const PATHS = {
  cart: ["M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"],
  claims: ["M23 7l-7 5 7 5V7z", "M14 5H3a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2z"],
  heart: ["M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"],
  user: ["M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2", "M12 3a4 4 0 1 0 0 8 4 4 0 0 0 0-8z"],
  orders: ["M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z", "M14 2v6h6"],
  lic: ["M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z", "M14 2v6h6", "M16 13H8", "M16 17H8"],
  deals: ["M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z", "M7 7h.01"],
  mail: ["M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z", "M22 6l-10 7L2 6"],
  dl: ["M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4", "M7 10l5 5 5-5", "M12 15V3"],
  creator: ["M5 3l14 9-14 9V3z"],
  gear: ["M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2", "M12 3a4 4 0 1 0 0 8 4 4 0 0 0 0-8z"],
  logout: ["M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4", "M16 17l5-5-5-5", "M21 12H9"],
  freeUse: ["M8 21h12a2 2 0 0 0 2-2v-2H10v2a2 2 0 1 1-4 0V5a2 2 0 1 0-4 0v3h4", "M19 17V5a2 2 0 0 0-2-2H4"],
  shield: ["M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z", "M9 12l2 2 4-4"],
  edit: ["M12 20h9", "M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"],
  mic: ["M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z", "M19 10v2a7 7 0 0 1-14 0v-2", "M12 19v4", "M8 23h8"],
  dollar: ["M12 1v22", "M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"],
  subs: ["M2 5h20a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1z", "M1 10h22", "M6 15h4"],
  help: ["M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z", "M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3", "M12 17h.01"],
  unlock: ["M7 11V7a5 5 0 0 1 9.9-1", "M5 11h14a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2z"],
  star: ["M12 2l2.9 6.3 6.9.7-5.1 4.6 1.4 6.8L12 17.3 5.9 20.4l1.4-6.8L2.2 9l6.9-.7z"],
  music: "M9 18V5l12-2v13 M6 21a3 3 0 1 0 0-6 3 3 0 0 0 0 6z M18 19a3 3 0 1 0 0-6 3 3 0 0 0 0 6z",
  youtube: ["M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33A2.78 2.78 0 0 0 3.4 19c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2 29 29 0 0 0 .46-5.25 29 29 0 0 0-.46-5.33z", "M9.75 15.02l5.75-3.27-5.75-3.27v6.54z"],
  diamond: ["M6 3h12l4 6-10 13L2 9Z", "M11 3 8 9l4 13 4-13-3-6", "M2 9h20"],
  sparkles: [
    "M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .962 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.962 0z",
    "M20 3v4",
    "M22 5h-4",
    "M4 17v2",
    "M5 18H3",
  ],
};

const CartIcon: FC<{ size?: number }> = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    <circle cx="9" cy="20.5" r="1.3" fill="none" /><circle cx="17.4" cy="20.5" r="1.3" fill="none" /><path d="M2 3h3l2.4 12.1a1.7 1.7 0 0 0 1.7 1.4h8.5a1.7 1.7 0 0 0 1.7-1.4L21 7.5H6" />
  </svg>
);


/* ── module-level helpers (declared before the component for no-use-before-define) ── */
const MusicNote: FC<{ size?: number }> = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 640 640" fill="currentColor" style={{ flexShrink: 0 }}>
    <path d="M532 71C539.6 77.1 544 86.3 544 96L544 400C544 444.2 501 480 448 480C395 480 352 444.2 352 400C352 355.8 395 320 448 320C459.2 320 470 321.6 480 324.6L480 207.9L256 257.7L256 464C256 508.2 213 544 160 544C107 544 64 508.2 64 464C64 419.8 107 384 160 384C171.2 384 182 385.6 192 388.6L192 160C192 145 202.4 132 217.1 128.8L505.1 64.8C514.6 62.7 524.5 65 532.1 71.1z" />
  </svg>
);

// Full-width tile that expands a connected dropdown — smooth max-height animation.
// MUST be module-level (stable identity) so the panel persists across renders
// and the transition runs; a component defined inside MobileHeader would remount.
const Expandable: FC<{ icon: React.ReactNode; label: string; open: boolean; onToggle: () => void; children: React.ReactNode }> = ({ icon, label, open, onToggle, children }) => (
  <div>
    <button onClick={onToggle}
      onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(0,0,0,0.1)"; (e.currentTarget.firstElementChild as HTMLElement).style.color = SIGNUP_BLUE; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(0,0,0,0.18)"; (e.currentTarget.firstElementChild as HTMLElement).style.color = SIGNUP_BLUE; }}
      style={{
      display: "flex", alignItems: "center", gap: 14, width: "100%", padding: "16px 18px",
      borderRadius: open ? "14px 14px 0 0" : 14,
      border: "1px solid rgba(235,194,142,0.18)", borderBottom: open ? "1px solid rgba(235,194,142,0.1)" : "1px solid rgba(235,194,142,0.18)",
      background: "rgba(0,0,0,0.18)", cursor: "pointer", color: colors.text.primary,
      fontFamily: '"Raleway", sans-serif', fontSize: 15.5, fontWeight: 500, textAlign: "left", transition: "border-radius 0.2s ease, background 0.2s ease",
    }}>
      {/* Collapsible (Expandable) menu items use a blue leading icon at rest. */}
      <span style={{ width: 46, height: 46, borderRadius: "50%", background: "rgba(255,220,168,0.12)", display: "inline-flex", alignItems: "center", justifyContent: "center", color: SIGNUP_BLUE, flexShrink: 0, transition: "color 0.2s ease" }}>{icon}</span>
      <span style={{ flex: 1 }}>{label}</span>
      <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke={ICON_GOLD} strokeWidth={2} style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 0.25s ease", flexShrink: 0 }}><path d="M6 9l6 6 6-6" /></svg>
    </button>
    <div style={{
      overflow: "hidden", maxHeight: open ? 600 : 0, opacity: open ? 1 : 0,
      transition: "max-height 0.32s cubic-bezier(0.4,0,0.2,1), opacity 0.25s ease",
      border: open ? "1px solid rgba(235,194,142,0.18)" : "none", borderTop: "none",
      borderRadius: "0 0 14px 14px", background: "rgba(255,255,255,0.02)",
    }}>
      <div style={{ padding: "6px 8px" }}>{children}</div>
    </div>
  </div>
);

const MobileHeader: FC<Props> = ({ handleOpenDialog }) => {
  usePresenceHeartbeat(); // bump lastSeenAt on mobile too (desktop did this in SecondHeader)
  const isTablet = useMediaQuery("(min-width:600px)"); // 600–1149 (component only mounts <1150)
  const { isLoggedIn, isLoading } = useAuth();
  const { accountType } = useAccountType();
  // Plan-aware questionnaire item — mirrors the /music "Free Use or License?"
  // link: subscribers get "Subscription or License?", music creators don't see it.
  const isSub = accountType === AccountType.SUBSCRIPTION;
  const isMusicCreator = accountType === AccountType.MUSIC_CREATOR;
  const actionRequiredCount = useActionRequiredCount();
  const cartItems = useAppSelector((s) => s.cart.items);
  const cartCount = cartItems.reduce((sum, i) => sum + i.quantity, 0);
  const whitelistingSuspended = useWhitelistingSuspended();
  const goFixWhitelisting = useWhitelistingPausedRedirect();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  // The bar is `position: fixed` (not `sticky`) to avoid iOS Safari's 1px
  // sub-pixel scroll seam at a sticky element's bottom edge. Because fixed
  // removes it from flow, an in-flow spacer of the bar's measured height holds
  // the space below it — so pages don't need their own top padding (same as
  // the old sticky behaviour). Default to the row height (no banners) to avoid
  // a first-paint jump; the ResizeObserver refines it for banner heights.
  const barRef = useRef<HTMLDivElement>(null);
  const [barHeight, setBarHeight] = useState<number>(isTablet ? 73 : 65);
  useLayoutEffect(() => {
    const el = barRef.current;
    if (!el) return;
    const measure = () => setBarHeight(el.getBoundingClientRect().height);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const [mainOpen, setMainOpen] = useState(false);
  // Suppresses the slide animation for a single snap-close; an rAF resets it so
  // the normal ✕/scrim close still animates. (Previously also used to hand off
  // between the ☰ and a mobile-only user drawer — the account menu is now the
  // shared desktop dropdown, so only the ☰ drawer remains.)
  const [mainInstant, setMainInstant] = useState(false);
  useEffect(() => {
    if (!mainInstant) return;
    const id = requestAnimationFrame(() => setMainInstant(false));
    return () => cancelAnimationFrame(id);
  }, [mainInstant]);
  const openMain = () => setMainOpen(true);
  const [openTile, setOpenTile] = useState<"free" | "licensing" | "contact" | null>(null);
  const toggleTile = (key: "free" | "licensing" | "contact") => setOpenTile((cur) => (cur === key ? null : key));
  const loggedIn = isLoggedIn && !isLoading;

  const go = (route: string) => {
    setMainOpen(false);
    setTimeout(() => navigate(route), 260);
  };

  /* ── shared bits ── */
  const badge = (n: number, blue = false) => (
    <span style={{
      position: "absolute", top: -4, right: -6, minWidth: 17, height: 17, padding: "0 5px",
      borderRadius: 9, background: blue ? BLUE : "#fff", color: blue ? BLUE_DEEP : "#3c2d23",
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
      fontSize: 10, fontWeight: 600, lineHeight: "17px", display: "flex", alignItems: "center", justifyContent: "center",
    }}>{n > 99 ? "99+" : n}</span>
  );

  const iconBtnStyle: React.CSSProperties = {
    width: 38, height: 38, borderRadius: "50%", border: "1.5px solid rgba(255,255,255,0.5)",
    background: "transparent", color: "rgba(255,255,255,0.85)", display: "inline-flex",
    alignItems: "center", justifyContent: "center", cursor: "pointer", position: "relative", flexShrink: 0,
  };

  const CartBtn = () => (
    <button
      aria-label="Cart"
      onClick={() => { if (whitelistingSuspended) return goFixWhitelisting(); dispatch(toggleCart()); }}
      title={whitelistingSuspended ? WHITELISTING_PAUSED_HINT : undefined}
      // Blue cart icon when the cart has at least one item (matches the desktop header).
      style={{ ...iconBtnStyle, color: cartCount > 0 ? "#9cd3fd" : iconBtnStyle.color }}
    >
      <CartIcon />{cartCount > 0 && badge(cartCount)}
    </button>
  );

  const ClaimsPill = () => (
    <Link to={ERoutes.AccountVideos} aria-label="Claims" style={{
      height: 38, padding: "0 13px", borderRadius: 999,
      border: `1.5px solid ${actionRequiredCount > 0 ? BLUE : "rgba(255,255,255,0.5)"}`,
      background: actionRequiredCount > 0 ? BLUE : "transparent",
      color: actionRequiredCount > 0 ? BLUE_DEEP : "rgba(255,255,255,0.85)",
      fontFamily: '"Raleway", sans-serif', fontSize: 12.5, fontWeight: 600, letterSpacing: "0.3px",
      display: "inline-flex", alignItems: "center", textDecoration: "none", position: "relative",
      flexShrink: 0, whiteSpace: "nowrap",
    }}>
      Claims{actionRequiredCount > 0 && badge(actionRequiredCount)}
    </Link>
  );

  /* The account menu is the SHARED desktop component (`AccountMenu`), not a
     mobile-specific one — same trigger, same dropdown, same items at every
     width. It brings its own avatar button and popper; the only mobile
     concession is that the dropdown scrolls when it is taller than the
     viewport, which AccountMenu handles behind a <1150px media query. */
  const Avatar = () => <AccountMenu />;

  const Burger = () => (
    <button aria-label="Open menu" onClick={openMain} style={{
      width: 38, height: 38, borderRadius: 9, border: "1px solid rgba(186,144,92,0.35)",
      background: "transparent", color: "rgba(251,244,236,0.8)", display: "inline-flex",
      alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0,
    }}>
      <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round">
        <line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" />
      </svg>
    </button>
  );

  const SignUpBtn = () => (
    <button onClick={() => handleOpenDialog(false)} style={{
      height: 38, padding: "0 18px", display: "inline-flex", alignItems: "center", justifyContent: "center",
      background: SIGNUP_BLUE, border: "none", color: "#1a2a3a", fontFamily: '"Raleway", sans-serif',
      fontSize: 13, fontWeight: 600, borderRadius: 50, cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0,
    }}>Sign up</button>
  );
  const LogInBtn = () => (
    <button onClick={() => handleOpenDialog(true)} style={{
      height: 38, padding: "0 18px", display: "inline-flex", alignItems: "center", justifyContent: "center",
      background: GOLD_BTN, border: "none", color: "#3c2d23",
      fontFamily: '"Raleway", sans-serif', fontSize: 13, fontWeight: 600, borderRadius: 50,
      cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0,
    }}>Log in</button>
  );
  // Phone primary CTA — opens the login dialog (signup is still in the drawer)
  const SignInBtn = () => (
    <button onClick={() => handleOpenDialog(true)} style={{
      height: 38, padding: "0 18px", display: "inline-flex", alignItems: "center", justifyContent: "center",
      background: SIGNUP_BLUE, border: "none", color: "#1a2a3a", fontFamily: '"Raleway", sans-serif',
      fontSize: 13, fontWeight: 600, borderRadius: 50, cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0,
    }}>Sign in</button>
  );

  const row1H = isTablet ? 72 : 64; // header bar height — more top/bottom breathing room
  /* ════════════ BAR ════════════ */
  const bar = (
    <div ref={barRef} data-app-header="" style={{
      // Fixed (not sticky) — see barHeight note above: avoids iOS Safari's 1px
      // sub-pixel scroll seam at a sticky element's bottom edge. The in-flow
      // spacer rendered after this reserves its height.
      // `top` follows the support-view bar, which is fixed above everything —
      // otherwise this bar sits underneath it and the page shows a blank strip
      // where the header should be. Zero for every ordinary visitor.
      position: "fixed", top: "var(--view-as-banner-h, 0px)", left: 0, right: 0, zIndex: 200, background: BROWN,
      borderBottom: "1px solid rgba(235,194,142,0.25)",
    }}>
      {/* row 1 */}
      <div style={{ height: row1H, display: "flex", alignItems: "center", padding: isTablet ? "0 20px" : "0 14px", gap: 10 }}>
        <Link to={loggedIn ? ERoutes.Catalog : ERoutes.Home} aria-label="Audio Sanctum home" style={{ display: "flex", alignItems: "center", flexShrink: 0, height: 32 }}>
          {isTablet
            ? <GeneralLogo style={{ height: 30, width: "auto" }} />
            : <MobileMenuLogo style={{ height: 32, width: "auto" }} />}
        </Link>
        <div style={{ flex: 1 }} />
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {isLoading ? (
            /* Auth not resolved yet — show only the neutral controls (cart +
               burger) so we never flash the signed-out CTAs (or hide the
               Claims pill as if guest) before /users/my lands. */
            <>
              <CartBtn />
              <Burger />
            </>
          ) : loggedIn ? (
            <>
              <ClaimsPill />
              {/* Music Creators license via their program, not the cart (A1) */}
              {!isMusicCreator && <CartBtn />}
              <Avatar />
              <Burger />
            </>
          ) : (
            <>
              <CartBtn />
              {isTablet ? (
                <>
                  <LogInBtn />
                  <SignUpBtn />
                </>
              ) : (
                <SignInBtn />
              )}
              <Burger />
            </>
          )}
        </div>
      </div>

      {/* Global banners — same set SecondHeader mounts on desktop. They live
          INSIDE the sticky bar so they show + stick with the header on mobile.
          Each reserves its own height in-flow here (the body padding-top offset
          is zeroed below 1150px in index.css to avoid double-spacing). The
          `ms-mobile-banner-host` class lets index.css compact these
          desktop-built banners on mobile (stack text over button + smaller
          type) so their long copy doesn't wrap into a tall strip. */}
      <div className="ms-mobile-banner-host">
        <GlobalIncidentBanner />
        <PendingEmailGlobalBanner />
        <BetaGlobalBanner />
        <PastDueGlobalBanner />
        <WhitelistingSuspendedGlobalBanner />
        <CancelledGlobalBanner />
        <DisconnectChannelsReminderBanner />
        <DowngradeSuccessBanner />
        <AccountRecoveredGlobalBanner />
      </div>
    </div>
  );

  /* ════════════ DRAWERS ════════════ */
  const scrim = (open: boolean, onClose: () => void, topOffset = 0, instant = false) => (
    <div onClick={onClose} style={{
      // topOffset keeps the sticky header bar uncovered (so it stays visible +
      // clickable) for the top-drop user menu.
      // Above the credit-recheck modal overlay (z1300) so the main menu opens on
      // top of the "services paused" modal, matching the user menu.
      position: "fixed", top: topOffset, left: 0, right: 0, bottom: 0, background: "rgba(28,18,10,0.55)", zIndex: 1490,
      opacity: open ? 1 : 0, pointerEvents: open ? "auto" : "none", transition: instant ? "none" : "opacity 0.3s ease",
    }} />
  );

  // `fromTop` makes the panel drop down from BEHIND the sticky header bar (used for
  // the user/account menu); the default slides in from the right (the main menu).
  const drawerShell = (open: boolean, onClose: () => void, body: React.ReactNode, foot?: React.ReactNode, fromTop = false, headerLeft?: React.ReactNode, instant = false) => {
    const panel = (
      <div style={fromTop ? {
        // Lives inside the clip wrapper (anchored at the bar's bottom) so it
        // emerges from behind the header and sits flush — no gap.
        position: "relative", width: "100%", maxHeight: "100%",
        pointerEvents: "auto",
        background: components.modal.background,
        borderBottom: "1px solid rgba(235,194,142,0.15)", borderRadius: "0 0 22px 22px",
        display: "flex", flexDirection: "column", overflow: "hidden",
        transform: open ? "translateY(0)" : "translateY(-100%)", transition: instant ? "none" : "transform 0.32s cubic-bezier(0.4,0,0.2,1)",
        boxShadow: open ? "0 18px 50px rgba(0,0,0,0.45)" : "none",
      } : {
        position: "fixed", top: 0, right: 0, height: "100dvh", width: isTablet ? 480 : "calc(100% - 56px)",
        maxWidth: "100%", background: components.modal.background, borderLeft: "1px solid rgba(235,194,142,0.15)",
        zIndex: 1500, display: "flex", flexDirection: "column",
        transform: open ? "translateX(0)" : "translateX(100%)", transition: instant ? "none" : "transform 0.32s cubic-bezier(0.4,0,0.2,1)",
        /* only when open — otherwise the off-screen drawer's left-projecting shadow
           bleeds ~50px back onto the right edge of every page */
        boxShadow: open ? "-18px 0 50px rgba(0,0,0,0.45)" : "none",
      }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: headerLeft ? "space-between" : "flex-end", gap: 12, padding: headerLeft ? "16px 12px 14px 20px" : "12px 12px 4px", flexShrink: 0, borderBottom: headerLeft ? "1px solid rgba(235,194,142,0.15)" : "none" }}>
          {headerLeft}
          <button aria-label="Close" onClick={onClose} style={{ width: 52, height: 52, borderRadius: 8, border: "none", background: "transparent", color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <svg width={26} height={26} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.3} strokeLinecap="round"><line x1="6" y1="6" x2="18" y2="18" /><line x1="18" y1="6" x2="6" y2="18" /></svg>
          </button>
        </div>
        <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: fromTop ? "16px 16px 16px" : "4px 16px 16px" }}>{body}</div>
        {foot && <div style={{ borderTop: "1px solid rgba(235,194,142,0.15)", padding: "10px 16px 16px", flexShrink: 0 }}>{foot}</div>}
      </div>
    );
    if (!fromTop) return panel;
    // Clip wrapper anchored at the bar's bottom: hides the panel's top so it
    // appears to slide out from behind the header (pointer-events pass through
    // the empty area to the scrim for tap-to-close).
    return (
      <div style={{ position: "fixed", top: row1H - 3, left: 0, right: 0, bottom: 0, zIndex: 1300, overflow: "hidden", pointerEvents: "none" }}>
        {panel}
      </div>
    );
  };

  const Row: FC<{ icon: React.ReactNode; label: string; onClick?: () => void; to?: string; badgeEl?: React.ReactNode }> = ({ icon, label, onClick, to, badgeEl }) => {
    const content = (
      <>
        <span style={{ display: "inline-flex", color: ICON_GOLD }}>{icon}</span>
        <span style={{ flex: 1 }}>{label}</span>
        {badgeEl}
      </>
    );
    const style: React.CSSProperties = { display: "flex", alignItems: "center", gap: 12, padding: 12, borderRadius: 10, color: colors.text.primary, fontFamily: '"Raleway", sans-serif', fontSize: 14.5, fontWeight: 500, cursor: "pointer", textDecoration: "none", width: "100%", background: "transparent", border: "none", textAlign: "left", transition: "background 0.2s ease" };
    const onEnter = (e: React.MouseEvent<HTMLElement>) => { e.currentTarget.style.background = "rgba(255,255,255,0.06)"; };
    const onLeave = (e: React.MouseEvent<HTMLElement>) => { e.currentTarget.style.background = "transparent"; };
    return to
      ? <Link to={to} onClick={(e) => { if (onClick) { e.preventDefault(); onClick(); } }} onMouseEnter={onEnter} onMouseLeave={onLeave} style={style}>{content}</Link>
      : <button onClick={onClick} onMouseEnter={onEnter} onMouseLeave={onLeave} style={style}>{content}</button>;
  };

  const Tile: FC<{ icon: React.ReactNode; label: string; onClick: () => void; badgeEl?: React.ReactNode; bg?: string; bgHover?: string; muted?: boolean; blueIcon?: boolean }> = ({ icon, label, onClick, badgeEl, bg, bgHover, muted = false, blueIcon = false }) => {
    // `muted` = a held-back tile (e.g. Log out): no fill, softer border and a
    // toned-down icon so it reads calmer than the navigation tiles while
    // keeping the same tile shape.
    // `blueIcon` = main-menu tiles use a blue leading icon (the account drawer
    // tiles stay gold).
    const restBg = bg ?? (muted ? "transparent" : "rgba(0,0,0,0.18)");
    const hoverBg = bgHover ?? (muted ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.1)");
    const restIcon = muted ? "rgba(251,244,236,0.5)" : (blueIcon ? SIGNUP_BLUE : ICON_GOLD);
    return (
      <button onClick={onClick}
        onMouseEnter={(e) => { e.currentTarget.style.background = hoverBg; (e.currentTarget.firstElementChild as HTMLElement).style.color = SIGNUP_BLUE; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = restBg; (e.currentTarget.firstElementChild as HTMLElement).style.color = restIcon; }}
        style={{
        position: "relative", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-start",
        gap: 10, padding: "18px 10px", borderRadius: 14, cursor: "pointer", textAlign: "center", width: "100%",
        border: `1px solid ${muted ? "rgba(235,194,142,0.10)" : "rgba(235,194,142,0.18)"}`, background: restBg, transition: "background 0.2s ease",
        color: muted ? "rgba(251,244,236,0.6)" : colors.text.primary, fontFamily: '"Raleway", sans-serif', fontSize: 13.5, fontWeight: 500, lineHeight: 1.25,
      }}>
        <span style={{ width: 46, height: 46, borderRadius: "50%", background: muted ? "rgba(255,255,255,0.04)" : "rgba(255,220,168,0.12)", display: "inline-flex", alignItems: "center", justifyContent: "center", color: restIcon, transition: "color 0.2s ease" }}>{icon}</span>
        <span>{label}</span>
        {badgeEl && <span style={{ position: "absolute", top: 8, right: 8 }}>{badgeEl}</span>}
      </button>
    );
  };
  // Full-width tile (icon left + label) — prominent stacked entries.
  const WideTile: FC<{ icon: React.ReactNode; label: string; onClick: () => void; badgeEl?: React.ReactNode; borderColor?: string }> = ({ icon, label, onClick, badgeEl, borderColor }) => (
    <button onClick={onClick}
      onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(0,0,0,0.1)"; (e.currentTarget.firstElementChild as HTMLElement).style.color = SIGNUP_BLUE; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(0,0,0,0.18)"; (e.currentTarget.firstElementChild as HTMLElement).style.color = SIGNUP_BLUE; }}
      style={{
      display: "flex", alignItems: "center", gap: 14, width: "100%", padding: "16px 18px", borderRadius: 14,
      border: `1px solid ${borderColor || "rgba(235,194,142,0.18)"}`, background: "rgba(0,0,0,0.18)", cursor: "pointer", transition: "background 0.2s ease",
      color: colors.text.primary, fontFamily: '"Raleway", sans-serif', fontSize: 15.5, fontWeight: 500, textAlign: "left",
    }}>
      {/* Main-menu leading icons are blue at rest (site-wide menu icon colour). */}
      <span style={{ width: 46, height: 46, borderRadius: "50%", background: "rgba(255,220,168,0.12)", display: "inline-flex", alignItems: "center", justifyContent: "center", color: SIGNUP_BLUE, flexShrink: 0, transition: "color 0.2s ease" }}>{icon}</span>
      <span style={{ flex: 1 }}>{label}</span>
      {badgeEl}
    </button>
  );


  /* MAIN MENU body — big tiles grouped by section */
  const mainBody = (
    <>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <WideTile icon={<MusicNote size={22} />} label="Browse Music" onClick={() => go(ERoutes.Catalog)} />

      <WideTile icon={<Ic d={PATHS.sparkles} size={22} />} label="Features" onClick={() => go(ERoutes.HowItWorks)} />

      {/* Hide the subscription upsell once the user is logged in on a paid plan. */}
      {(!loggedIn || accountType === AccountType.FREE) && (
        <WideTile icon={<Ic d={PATHS.diamond} size={22} />} label="Start Your Subscription" onClick={() => go(ERoutes.Pricing)} />
      )}

      <Expandable icon={<Ic d={PATHS.youtube} size={22} />} label="Free YouTube Music" open={openTile === "free"} onToggle={() => toggleTile("free")}>
        {!isMusicCreator && (
          <Row icon={<Ic d={PATHS.shield} />} label={isSub ? "Subscription or License?" : "Free, Subscription or License?"} onClick={() => go(ERoutes.Test)} badgeEl={<span style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: "0.5px", textTransform: "uppercase", padding: "2px 7px", borderRadius: 9, background: colors.link.blue, color: "#193e5b" }}>Test</span>} />
        )}
        <Row icon={<Ic d={PATHS.youtube} />} label="YouTube Use" onClick={() => go(ERoutes.FreeUseTerms)} />
      </Expandable>

      <Expandable icon={<Ic d={PATHS.freeUse} size={22} />} label="Licensing" open={openTile === "licensing"} onToggle={() => toggleTile("licensing")}>
        <Row icon={<Ic d={PATHS.freeUse} />} label="Licensing" onClick={() => go(ERoutes.CatalogLandingLicensing)} />
        <Row icon={<Ic d={PATHS.music} />} label="Music & Ambient Creators" onClick={() => go(ERoutes.MUSIC_CREATOR_PROGRAM)} />
        <Row icon={<Ic d={PATHS.mail} />} label="License Request" onClick={() => go(ERoutes.Licensing)} />
      </Expandable>

      <Expandable icon={<Ic d={PATHS.mail} size={22} />} label="Contact" open={openTile === "contact"} onToggle={() => toggleTile("contact")}>
        <Row icon={<Ic d={PATHS.mail} />} label="Contact Form" onClick={() => go(ERoutes.Contact)} />
        <Row icon={<Ic d={PATHS.mail} />} label="License Request" onClick={() => go(ERoutes.Licensing)} />
        <Row icon={<Ic d={PATHS.mic} />} label="Submit Music" onClick={() => go(ERoutes.Submit)} />
      </Expandable>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 20, marginTop: 10 }}>
        <Tile blueIcon icon={<Ic d={PATHS.mail} size={22} />} label="Newsletter" onClick={() => go(loggedIn ? `${ERoutes.AccountNotifications}#newsletter` : ERoutes.Newsletter)} bg="rgba(235,194,142,0.14)" bgHover="rgba(235,194,142,0.22)" />
        <Tile blueIcon icon={<Ic d={PATHS.deals} size={22} />} label="Deals" onClick={() => go(ERoutes.Deals)} bg="rgba(235,194,142,0.14)" bgHover="rgba(235,194,142,0.22)" />
      </div>
      </div>
    </>
  );

  return (
    <>
      {bar}
      {/* In-flow spacer reserving the fixed bar's height (so page content sits
          below it, exactly as the old sticky bar did). */}
      <div aria-hidden style={{ height: barHeight, flexShrink: 0 }} />
      {/* Modal hosts the banners drive (past-due → update payment; pending
          downgrade → channel picker). Desktop mounts these in SecondHeader. */}
      <GlobalUpdatePaymentModal />
      <PendingDowngradeGlobalModal />
      {scrim(mainOpen, () => setMainOpen(false), 0, mainInstant)}
      {drawerShell(mainOpen, () => setMainOpen(false), mainBody, undefined, false, undefined, mainInstant)}
    </>
  );
};

export default MobileHeader;
